import { useCallback, useEffect, useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import MuiListItem from '@material-ui/core/ListItem';
import PrintTwoToneIcon from '@material-ui/icons/PrintTwoTone';
import {
  faCoins,
  faCreditCard,
  faEnvelope,
  faHandHoldingUsd,
  faHome,
  faPhone,
  faUser,
  faUtensils,
} from '@fortawesome/fontawesome-free-solid';
import moment from 'moment';
import _ from 'lodash';
import Paper from '../Paper';
import Button from '../Button';
import ListItem from '../ListItem';
import Spinner from '../Spinner';
import Modal from '../Modal';
import 'react-notifications-component/dist/theme.css';
import * as actions from '../../actions/index';
import { displayDate, suitableTime } from '../../utils/suitableDate';
import {
  CANCEL_CLIENT_UNAVAILABLE,
  CANCELLATION_LABELS,
  DELIVEROO,
  DRIVER_CANCEL_REASSIGNMENT,
  FLEET,
  METHODS,
  ORDER_CANCELED_LABEL,
  ORDER_COMPLETED_LABEL,
  ORDER_DELIVEROO,
  ORDER_FLOW_TYPE,
  ORDER_MISSED_LABEL,
  ORDER_PHASE_CONTACTING,
  ORDER_PHASE_CONTACTING_LABEL,
  ORDER_PHASE_DELIVERING_LABEL,
  ORDER_PHASE_LOOKING_FOR_DRIVER_LABEL,
  ORDER_PHASE_PREPARING_LABEL,
  ORDER_PHASE_TAKEAWAY_LABEL,
  ORDER_PHASE_WAITER_LABEL,
  ORDER_REFUND_LABEL,
  ORDER_SHOP_CANCELED_LABEL,
  ORDER_TYPE_DELIVERY,
  ORDER_TYPE_SEAT,
  ORDER_UBEREATS,
  PAYMENT_TYPE_STRIPE,
  PICKUP_STATUS,
  PICKUP_STATUS_KEY,
  SHOP_CANCEL_AND_REFUND,
  SHOP_CANCEL_PHASE_CONTACTING,
  SUITABLE_ORDER_TYPES,
  SUITABLE_PAYMENT_TYPES,
  UNIT,
  DRIVER,
  ORDER_PHASE_PREPARING,
  DEFAULT_SLOT_INTERVAL,
  DEFAULT_DELIVERY_TIME,
  DEFAULT_PREPARATION_TIME,
} from '../../constants';
import { mainColor } from '../../constants/theme';
import { getAddressFormatted } from '../../utils/mapUtils';
import { displayPriceFormat, isObjectNotEmpty } from '../../utils/tools';
import {
  getCanalName,
  isExternalApp,
  setOpacity,
} from '../../utils/orderUtils';
import { styles } from './OrderModal.styles';

import {
  canRefundOrder,
  findOptionParent,
  formatCategoryName,
  getCancellationReason,
  getDeliveryLogo,
  getSortedDrivers,
  getTrackingUrl,
  handleAcceptDelivery,
  handleCancelOrder,
  handleCustomerTakesOrder,
  handleLunchDelivery,
  handleNewOrder,
  handleOrderReady,
  handleRefundOrder,
  isAnyProviderEnabled,
  isDelivery,
  printTicket,
  pushOrdersToDrivers,
  returnNumber,
  shouldDisplayScheduledMessage,
  shouldDisplaySearchingDriverSection,
  getPaperClassName,
  shouldDisplayAcceptDelivery,
  getSplashOrderCondition,
} from './OrderModal.service';
import stuartLogo from '../../assets/pictures/stuartLogo.png';
import delivero from '../../assets/pictures/delivero.png';
import fleetLogo from '../../assets/pictures/fleet.png';
import { checkIntegrationAccess, hasExternalIntegration } from 'src/utils/externalIntegration';
import MapOrders from '../MapOrders';
import { getSearchingDriverStatus } from '../../components/OrderItem/OrderItem.services';
import uberDirectLogo from '../../assets/pictures/uberDirectLogo.png';
import { ScheduledOrderMessage } from './components/OrderDetailMessage';
import { renderPromotionBloc } from './components/OrderPromotion';
import OrderDetailLine from './components/OrderDetailLine'
import { MOMENT_UNIT } from 'src/constants/date';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { getDefaultPreparationTime, refundOrder } from 'src/apis/backend';
import UpdateDatePreparationEnded from './components/UpdateDatePreparationEnded';

const Item = withStyles({
  root: {
    padding: '7px',
    backgroundColor: mainColor.colorWhite,
    '&:hover': {
      backgroundColor: `rgba(100, 100, 100, 0.3)`,
    },
  },
})(MuiListItem);

export default function OrderModal({
  order,
  isOpen,
  handleClose,
  driversAvailability,
}) {
  const {
    datePreparationEnded,
    userServicePrice = 0,
    managementFee = 0,
    address = {},
    promotion = {},
    canal,
    firstName,
    lastName = '',
    numero,
    totalPriceOrder,
    orderType,
    email,
    date,
    expectedDropoffDate = null,
    status,
    userNameDep,
    shopId,
    externalOrderNumber,
    tableNb = '',
    pickupStatus,
    orderFlowType = ORDER_FLOW_TYPE.CLASSIC,
    shopResponsibleForDelivery,
  } = order;

  const [isLoadingReception, setIsLoadingReception] = useState(false);
  const [isLoadingSendingOrder, setIsLoadingSendingOrder] = useState(false);
  const [isLoadingDriver, setIsLoadingDriver] = useState(false);
  const [defaultPreparationTime, setDefaultPreparationTime] = useState(DEFAULT_PREPARATION_TIME)
  const [
    updateDatePrepEndDebounceTimer,
    setUpdateDatePrepEndDebounceTimer,
  ] = useState(null);


  const classes = styles();

  const { generalConfig = {}, shop = {} } = useAppSelector(
    (state) => state.firebaseRealTime,
  );

  const dispatch = useAppDispatch()

  const getBaseDatePreparationEnded = useCallback(() => {
    const defaultDeliveryTime = orderType === ORDER_TYPE_DELIVERY ? DEFAULT_DELIVERY_TIME : 0;

    const idealDatePrepEnd = moment(date).subtract(defaultDeliveryTime, MOMENT_UNIT.MINUTE)

    const basePrepEnd = moment().add(order.status === ORDER_PHASE_CONTACTING ? DEFAULT_PREPARATION_TIME : 0, MOMENT_UNIT.MINUTE)

    return { minDatePrepEnd: moment.max(moment(), idealDatePrepEnd), baseDatePrepEnd: moment.max(basePrepEnd, idealDatePrepEnd) }
  }, [orderType, date, order.status])


  const [stateDatePrepEnd, setStateDatePrepEnd] = useState(
    datePreparationEnded ? moment(datePreparationEnded) : getBaseDatePreparationEnded().baseDatePrepEnd,
  );

  useEffect(() => {
    return () => {
      if (updateDatePrepEndDebounceTimer) {
        clearTimeout(updateDatePrepEndDebounceTimer);
      }
    };
  }, [updateDatePrepEndDebounceTimer]);

  useEffect(() => {
    if (isOpen) {
      setStateDatePrepEnd(datePreparationEnded ? moment(datePreparationEnded) : getBaseDatePreparationEnded().baseDatePrepEnd)
    }
  }, [datePreparationEnded, getBaseDatePreparationEnded, isOpen])

  const { configuration = {} } = shop;
  const { delivery = {}, marketplace = {} } = generalConfig;
  const { fleet = {} } = delivery;
  const { showOrderInPreparation = false } = fleet;
  const { dashboard, autoAccept } = configuration;
  const { isAllShops, distributeurAdmin } = useAppSelector(
    (state) => state.user,
  );

  const statusLabel = getSearchingDriverStatus(order);
  const isCompleted =
    statusLabel === ORDER_CANCELED_LABEL ||
    statusLabel === ORDER_COMPLETED_LABEL ||
    statusLabel === ORDER_MISSED_LABEL ||
    statusLabel === ORDER_REFUND_LABEL ||
    statusLabel === ORDER_SHOP_CANCELED_LABEL;
  const momentDate = moment(datePreparationEnded || date);
  const time = suitableTime(momentDate);
  const { type, promoCode, reconversionType } = promotion;
  const isDisplayDriverDetails = (() => {
    const isDelivering = statusLabel === ORDER_PHASE_DELIVERING_LABEL;
    const isPreparingAndPickedUp =
      statusLabel === ORDER_PHASE_PREPARING_LABEL && pickupStatus > 0;
    const isContactingAndReverseFlow =
      statusLabel === ORDER_PHASE_CONTACTING_LABEL &&
      pickupStatus > 0 &&
      orderFlowType === ORDER_FLOW_TYPE.REVERSE;
    return isDelivering || isPreparingAndPickedUp || isContactingAndReverseFlow;
  })();

  const isDisplayInformations =
    !isCompleted && order?.delivery?.searchDriver && order?.selfPickupEligible;
  const cancellationBy = getCancellationReason(order);
  const shouldHideOrderPhase =
    order?.delivery?.nature?.toUpperCase() === DELIVEROO &&
    isDelivery(orderType) &&
    pickupStatus > 0 &&
    !shopResponsibleForDelivery;
  const shouldDisplayLookingForDriver =
    order?.deliveryAcceptableByShop ||
    !order?.delivery?.searchDriver ||
    order?.selfPickupEligible;

  useEffect(() => {
    const fetch = async () => {
      const defaultPrepTime = await getDefaultPreparationTime()

      setDefaultPreparationTime(defaultPrepTime)
    }

    fetch()
  }, [])

  const renderHistoryButton = (currentOrder) => {
    return (
      <Button
        color='secondary'
        className='btn-xs-block'
        style={{ marginRight: -10 }}
        onClick={() => {
          const modalData = {
            btnColor: 'secondary',
            color: 'secondary',
            title: `Historique de recherche de livreurs`,
            header: true,
            children: renderDriversHistory(currentOrder?.driversHistory),
            center: true,
            btnTxt: 'Retour',
            width: '590px',
            height: '200px',
          };
          dispatch(actions.showModal(modalData));
        }}
      >
        Historique de recherche
      </Button>
    );
  };
  const displaySearchingDriver = (currentOrder) => {
    const renderSearchingTitle = () => (
      <div className='d-flex align-items-center justify-content-between'>
        <p style={classes.subtitle}>En recherche de livreurs</p>
        {currentOrder?.driversHistory?.some((driver) => driver.name) && (
          <div className='d-flex'>{renderHistoryButton(currentOrder)}</div>
        )}
      </div>
    );

    const renderSearchDriverButton = () => (
      <>
        <p className={`pb-1`} style={classes.subtitle}>
          Chercher un livreur
        </p>
        <div
          className='d-flex align-items-center justify-content-center'
          style={{ minHeight: 85 }}
        >
          <Button
            color='success'
            outlined
            padding='sm'
            minWidth='0px'
            onClick={() =>
              pushOrdersToDrivers(
                currentOrder,
                driversAvailability,
                isAllShops,
                isLoadingSendingOrder,
                setIsLoadingSendingOrder,
              )
            }
            disabled={
              currentOrder?.delivery?.searchDriver || isLoadingSendingOrder
            }
            style={{ width: '324px', ...classes.searchDriver }}
            isLoading={isLoadingSendingOrder}
          >
            Envoyer aux livreurs
          </Button>
        </div>
      </>
    );

    const renderStatus = (logo, name, content) => (
      <div className='d-flex align-items-center justify-content-between'>
        <div>
          <img src={logo} alt={`${name} Logo`} style={classes.animatedLogo} />
          <b className='pl-3'>{name}</b>
        </div>
        {content && content}
      </div>
    );

    const renderStatusList = () => {
      const { deliveroo, stuart, uberDirect, driversHistory } =
        currentOrder || {};
      const hasDriverContact = driversHistory?.some((driver) => driver.name);
      const sortedDrivers = getSortedDrivers(driversHistory) || [];
      const driverName = sortedDrivers[0]?.name;

      const getActiveService = () => {
        if (deliveroo?.jobId) return { logo: delivero, name: 'Deliveroo' };
        if (uberDirect?.jobId)
          return { logo: uberDirectLogo, name: 'Uber Direct' };
        if (stuart?.jobId) return { logo: stuartLogo, name: 'Stuart' };
        if (currentOrder?.delivery?.jobId) {
          return {
            logo: fleetLogo,
            name: 'Dishop Fleet',
            extra: hasDriverContact && (
              <span className='pl-5'>
                En contact avec <b>{driverName}</b>
                <div className='dot-animation'></div>
              </span>
            ),
          };
        }
        return null;
      };

      const activeService = getActiveService();

      if (!activeService && _.size(sortedDrivers) === 0) {
        return (
          <div className='d-flex flex-column' style={{ gap: UNIT * 1 }}>
            <div className='d-flex align-items-center justify-content-between'>
              <p style={classes.subtitle}>En recherche de livreurs</p>
            </div>
            <div className='text-muted'> Lancement de la recherche...</div>
          </div>
        );
      }

      return (
        <div className='d-flex flex-column' style={{ gap: UNIT * 1 }}>
          {renderSearchingTitle()}
          {renderStatus(
            activeService?.logo,
            activeService?.name,
            activeService?.extra,
          )}
        </div>
      );
    };

    return (
      <div style={classes.dynMarg}>
        {isLoadingDriver ? (
          <div
            className='d-flex align-self-center align-items-center justify-content-center'
            style={{ minHeight: 85 }}
          >
            <Spinner size={20} />
          </div>
        ) : (currentOrder?.statusLabel === ORDER_PHASE_PREPARING_LABEL ||
          getSearchingDriverStatus(currentOrder) ===
          ORDER_PHASE_LOOKING_FOR_DRIVER_LABEL) &&
          !currentOrder?.delivery?.searchDriver ? (
          renderSearchDriverButton()
        ) : (
          renderStatusList()
        )}
      </div>
    );
  };

  const displayLookingForDriver = (currentOrder) => {
    if (shouldDisplayAcceptDelivery(currentOrder)) {
      return (
        <div style={classes.dynMarg}>
          <div className='col-12 my-4 d-flex align-items-center justify-content-center'>
            <Button
              color='success'
              padding='sm'
              minWidth='0px'
              className='m-0 py-md-3 py-lg-2 py-sm-3 px-3'
              style={{ fontSize: UNIT * 0.9375, borderWidth: '2px' }}
              onClick={() => handleAcceptDelivery(currentOrder, setIsLoadingReception,
              )}
            >
              Je prends en charge la responsabilité de la livraison
            </Button>
          </div>
          <div className='d-flex mb-4 align-items-center justify-content-center'>
            <Button
              outlined
              color='danger'
              padding='sm'
              minWidth='0px'
              className='m-0 py-md-3 py-lg-2 py-sm-3 px-3'
              onClick={() =>
                handleCancelOrder(
                  currentOrder,
                  SHOP_CANCEL_PHASE_CONTACTING,
                  driversAvailability,
                  isAllShops,
                  setIsLoadingReception,
                  setIsLoadingDriver,
                )
              }
            >
              <p>Refuser la commande</p>
            </Button>
          </div>
        </div>
      );
    }

    if (
      !isLoadingDriver &&
      !isLoadingReception &&
      shouldDisplayScheduledMessage(currentOrder)
    ) {
      return renderScheduledMessage(currentOrder);
    }
    if (
      !currentOrder?.delivery?.searchDriver ||
      (currentOrder?.delivery?.searchDriver &&
        !shouldDisplayAcceptDelivery(currentOrder))
    )
      return displaySearchingDriver(currentOrder);
    else
      return (
        <div
          className='d-flex align-self-center align-items-center justify-content-center'
          style={{ minHeight: 85 }}
        >
          <Spinner size={20} />
        </div>
      );
  };

  const renderScheduledMessage = (currentOrder) => {
    if (!currentOrder) return null;

    return (
      <div className='d-flex flex-column align-items-center justify-content-center my-4'>
        <p style={{ textAlign: 'center' }}>
          Cette commande est programmée pour{' '}
          <strong>{`${suitableTime(moment(currentOrder.date))}`}</strong>.
          <br />
          Nous commencerons à chercher un livreur <strong>
            30 minutes
          </strong>{' '}
          avant la date souhaitée par le client.
        </p>
      </div>
    );
  };

  const displayPhaseOne = (currentOrder) => {
    const { canal } = currentOrder;
    return (
      <div style={classes.dynMarg}>
        {getSplashOrderCondition(currentOrder) && autoAccept?.enabled ? (
          <div
            className={`d-flex justify-content-center align-items-center my-4`}
          >
            <ScheduledOrderMessage classes={classes} order={currentOrder} />
          </div>
        ) : (
          <>
            {(!isExternalApp(canal) || canal === ORDER_UBEREATS) && (
              <UpdateDatePreparationEnded
                stateDatePrepEnd={stateDatePrepEnd}
                setStateDatePrepEnd={setStateDatePrepEnd}
                minDatePrepEnd={getBaseDatePreparationEnded().minDatePrepEnd}
                status={status}
                updateDatePrepEndDebounceTimer={updateDatePrepEndDebounceTimer}
                order={order}
                setUpdateDatePrepEndDebounceTimer={setUpdateDatePrepEndDebounceTimer} />
            )}
            <div className='d-flex my-4'>
              <div className='d-flex mr-3'>
                <Button
                  color='success'
                  padding='lg'
                  style={{
                    fontSize: UNIT,
                    padding: '10px 20px',
                    fontWeight: 'bolder',
                  }}
                  onClick={() =>
                    handleNewOrder(currentOrder, setIsLoadingReception, moment(stateDatePrepEnd).format())
                  }
                >
                  Accepter la commande
                </Button>
              </div>

              <div className='d-flex'>
                <Button
                  outlined
                  color='danger'
                  padding='sm'
                  minWidth='0px'
                  className='mr-0'
                  style={{ padding: '10px 20px' }}
                  onClick={() =>
                    handleCancelOrder(
                      currentOrder,
                      SHOP_CANCEL_PHASE_CONTACTING,
                      driversAvailability,
                      isAllShops,
                      setIsLoadingReception,
                      setIsLoadingDriver,
                    )
                  }
                >
                  <p>Refuser la commande</p>
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const displayPhaseTwo = (currentOrder) => {
    const {
      nbDriversAvailable = 0,
      nbDriversFreelance = 0,
    } = driversAvailability;
    const acceptDeliveryFromDashboard =
      dashboard?.validateDeliveryFromDashboard ?? isAllShops;
    const {
      orderType,
      canal,
      orderFlowType = ORDER_FLOW_TYPE.CLASSIC,
      shopResponsibleForDelivery,
    } = currentOrder;
    const showRefundButton = canRefundOrder(
      currentOrder,
      distributeurAdmin,
      marketplace,
    );
    const showDeliveryButton = !showOrderInPreparation;
    const isReverseOrderFlowType =
      orderFlowType === ORDER_FLOW_TYPE.REVERSE &&
      currentOrder?.pickupStatus ===
      PICKUP_STATUS_KEY.ORDER_NOT_CURRENTLY_SUPPORTED;
    const showAddTimeButton =
      (isDelivery(orderType) &&
        (((!showOrderInPreparation || acceptDeliveryFromDashboard) &&
          currentOrder?.pickupStatus ===
          PICKUP_STATUS_KEY.ORDER_NOT_CURRENTLY_SUPPORTED) ||
          shopResponsibleForDelivery)) ||
      (!isDelivery(orderType) && !isExternalApp(canal));

    return (
      <div style={classes.dynMarg}>
        {getSplashOrderCondition(currentOrder) ? (
          <ScheduledOrderMessage classes={classes} order={currentOrder} />
        ) : (
          <>
            {((isDelivery(orderType) &&
              (!showOrderInPreparation || acceptDeliveryFromDashboard)) ||
              !isDelivery(orderType)) &&
              !isExternalApp(canal) && (
                showAddTimeButton && (
                  <UpdateDatePreparationEnded
                    stateDatePrepEnd={stateDatePrepEnd}
                    setStateDatePrepEnd={setStateDatePrepEnd}
                    minDatePrepEnd={getBaseDatePreparationEnded().minDatePrepEnd}
                    status={status}
                    updateDatePrepEndDebounceTimer={updateDatePrepEndDebounceTimer}
                    order={order}
                    setUpdateDatePrepEndDebounceTimer={setUpdateDatePrepEndDebounceTimer} />
                )

              )}

            <div
              className='my-2 row d-flex align-self-center align-items-center justify-content-center'
              style={{ display: 'flex' }}
            >
              {isDelivery(orderType) ? (
                currentOrder?.pickupStatus ===
                  PICKUP_STATUS_KEY.ORDER_NOT_CURRENTLY_SUPPORTED ? (
                  <div className='row' style={{ justifyContent: 'center' }}>
                    {(shouldDisplayAcceptDelivery(currentOrder) ||
                      (!isReverseOrderFlowType &&
                        acceptDeliveryFromDashboard &&
                        !nbDriversAvailable &&
                        !nbDriversFreelance)) && (
                        <div
                          className={
                            showDeliveryButton || isReverseOrderFlowType
                              ? !showRefundButton || isReverseOrderFlowType
                                ? 'col-12 my-2'
                                : 'col-12 p-2'
                              : 'col-12 mt-3'
                          }
                          style={{ textAlign: 'center' }}
                        >
                          <Button
                            color='success'
                            padding='sm'
                            minWidth='0px'
                            onClick={() =>
                              handleAcceptDelivery(
                                currentOrder,
                                setIsLoadingReception,
                              )
                            }
                          >
                            Je prends en charge la responsabilité de la livraison
                          </Button>
                        </div>
                      )}
                  </div>
                ) : (
                  shopResponsibleForDelivery && (
                    <div
                      className={
                        showDeliveryButton || isReverseOrderFlowType
                          ? !showRefundButton || isReverseOrderFlowType
                            ? 'col-12'
                            : 'col-12 p-2'
                          : 'col-12 mt-3'
                      }
                      style={{ textAlign: 'center' }}
                    >
                      <Button
                        color='success'
                        padding='sm'
                        style={{
                          fontSize: UNIT * 1.1,
                          padding: '15px 50px',
                          fontWeight: 'bolder',
                          margin: 20,
                        }}
                        onClick={() =>
                          handleLunchDelivery(
                            currentOrder,
                            setIsLoadingReception,
                          )
                        }
                      >
                        Je démarre la livraison
                      </Button>
                    </div>
                  )
                )
              ) : (
                <Button
                  color='success'
                  padding='sm'
                  style={{
                    fontSize: UNIT * 1.1,
                    padding: '15px 50px',
                    fontWeight: 'bolder',
                    margin: 20,
                  }}
                  onClick={() =>
                    handleOrderReady(currentOrder, setIsLoadingReception)
                  }
                >
                  Commande prête
                </Button>
              )}
            </div>

            {!isExternalApp(canal) && renderCancelsButtons(currentOrder)}
          </>
        )}
      </div>
    );
  };

  const displayPhaseThree = (currentOrder) => {
    const validateDeliveryFromDashboard =
      dashboard?.validateDeliveryFromDashboard ?? isAllShops;
    const {
      canal,
      orderType,
      statusLabel,
      delivery = {},
      shopResponsibleForDelivery,
    } = currentOrder;
    const { searchDriver } = delivery;
    return (
      <div
        className={`${statusLabel !== ORDER_PHASE_DELIVERING_LABEL &&
          'mt-md-3'}`}
        style={isLoadingDriver ? classes.disableStyle : {}}
      >
        <p style={{ textAlign: 'center', ...classes.subtitle }}>
          {isDelivery(orderType)
            ? isExternalApp(canal)
              ? 'La commande est prête ?'
              : 'La livraison est terminée ?'
            : 'Le client est passé prendre sa commande ?'}
        </p>
        <div className={`row ml-0 mr-0  justify-content-center`}>
          {isAnyProviderEnabled(currentOrder) &&
            [
              PICKUP_STATUS_KEY.DRIVER_ON_THE_WAY_TO_RESTAURANT,
              PICKUP_STATUS_KEY.DRIVER_NEAR_TO_RESTAURANT,
            ].includes(currentOrder?.pickupStatus) && (
              <div className='mb-3 row  d-flex align-self-center align-items-center justify-content-center'>
                <Button
                  color='success'
                  outlined
                  padding='sm'
                  minWidth='0px'
                  style={classes.searchDriver}
                  disabled={searchDriver}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <p style={classes.message}>
                      {PICKUP_STATUS('')[currentOrder?.pickupStatus]}
                    </p>
                    {currentOrder?.pickupStatus ===
                      PICKUP_STATUS_KEY.DRIVER_ON_THE_WAY_TO_RESTAURANT ? (
                      <div className='pl-2'>
                        <img
                          src={getDeliveryLogo(currentOrder?.delivery?.nature)}
                          style={classes.logo}
                        />
                      </div>
                    ) : (
                      <Spinner
                        size={20}
                        style={{
                          color: mainColor.secondaryColor,
                          marginLeft: '10px',
                        }}
                      />
                    )}
                  </div>
                </Button>
              </div>
            )}
          {(shopResponsibleForDelivery ||
            validateDeliveryFromDashboard ||
            orderType !== ORDER_TYPE_DELIVERY) &&
            !isLoadingReception && (
              <div className='row  d-flex align-self-center align-items-center justify-content-center'>
                <Button
                  color='success'
                  padding='lg'
                  minWidth='0px'
                  className='py-3 px-6'
                  style={{
                    fontSize: UNIT * 1.2,
                    fontWeight: 'bolder',
                    margin: 20,
                  }}
                  onClick={() =>
                    handleCustomerTakesOrder(
                      currentOrder,
                      handleClose,
                      setIsLoadingReception,
                    )
                  }
                >
                  Valider la commande
                </Button>
              </div>
            )}
        </div>
        {!isExternalApp(canal) && renderCancelsButtons(currentOrder)}
      </div>
    );
  };

  const displayPhaseFour = (currentOrder) => {
    const { statusLabel, orderType } = currentOrder;
    const isPastOrder = statusLabel === ORDER_COMPLETED_LABEL;
    const showRefund =
      canRefundOrder(currentOrder, distributeurAdmin, marketplace) &&
      (statusLabel === ORDER_COMPLETED_LABEL ||
        statusLabel === ORDER_CANCELED_LABEL);
    return (
      showRefund && (
        <div
          className={`${orderType === ORDER_TYPE_SEAT ? 'd-inline-block d-sm-flex' : ''
            }`}
          style={{
            width: 'fit-content',
          }}
        >
          <ListItem
            title='Vous souhaitez rembourser votre client ?'
            icon={faHandHoldingUsd}
          />
          <div className='d-flex align-items-center justify-content-center'>
            {renderCancelButton(currentOrder, {
              typeCancel: 'rembourser',
              isCompleted: true,
              onClick: () => {
                handleRefundOrder(
                  currentOrder,
                  isPastOrder,
                  setIsLoadingReception,
                  refundOrder,
                );
              },
              btnTxt: 'Rembourser',
              color: 'secondary',
            })}
          </div>
        </div>
      )
    );
  };

  const displayOrderDetail = ({ title, body, color, isDate, description }) => {
    return (
      <div className='mb-2'>
        <p
          className={`${isDate && 'justify-content-xs-start'}`}
          style={classes.orderDetail}
        >
          <span style={{ whiteSpace: 'nowrap' }}>{title}</span>
          {body && (
            <>
              <b style={{ color, ...classes.boldText }}>{body}</b>
              {description && (
                <div style={classes.description}>{description}</div>
              )}
            </>
          )}
        </p>
      </div>
    );
  };

  const displayPaymentType = (order) => {
    const { paymentType, statusLabel } = order;
    const getPaymentText = () => {
      if (!paymentType) return 'Non payé';

      const isPaid =
        paymentType === PAYMENT_TYPE_STRIPE ||
        statusLabel === ORDER_COMPLETED_LABEL;
      const paymentStatus = isPaid ? 'Payé' : 'À payer';

      return `${paymentStatus} ${SUITABLE_PAYMENT_TYPES[paymentType]
        ?.replace('Paiement', '')
        ?.toLowerCase() || ''}`;
    };
    return (
      <div className='col px-0'>
        <p style={{ marginBottom: UNIT * 0.3125, ...classes.subtitle }}>
          MODE DE PAIEMENT
        </p>
        <ListItem
          title={getPaymentText()}
          icon={paymentType === 'cash' ? faCoins : faCreditCard}
          isbolder
        />
      </div>
    );
  };

  const renderCancelButton = (order, modalData) => {
    const { orderNumber, totalPriceOrder = 0 } = order;
    const {
      color = 'danger',
      title = null,
      onClick,
      btnTxt,
      btnColor = 'danger',
      showCancelBtn = true,
      typeCancel,
      isCompleted,
    } = modalData;
    const dataToUse = {
      color,
      title:
        title ||
        `Êtes-vous sûr(e) de vouloir ${typeCancel} la commande "${orderNumber}" d'un montant de ${displayPriceFormat(
          totalPriceOrder,
        )} ?`,
      showCancelBtn,
      btnColor,
      isOpen: true,
      handleClose: () => { dispatch(actions.showModal(null)) },
      onClick: () => {
        onClick();
        dispatch(actions.showModal(null));
      },
      size: 'md',
      btnTxt: 'Valider',
    };
    return isLoadingReception || (isLoadingDriver && title) ? (
      <Spinner size={20} />
    ) : (
      <Button
        color={color}
        outlined
        variant='contained'
        padding={!isCompleted ? 'sm' : 'xs'}
        minWidth='0px'
        onClick={() => dispatch(actions.showModal(dataToUse))}
      >
        {btnTxt} {!isCompleted && !title ? 'la commande' : null}
      </Button>
    );
  };

  const renderFollowDriverButton = (currentOrder) => {
    const followLink = getTrackingUrl(currentOrder);
    if (
      !followLink &&
      currentOrder.delivery.nature &&
      currentOrder?.delivery?.nature?.toUpperCase() !== FLEET
    )
      return null;
    return (
      <Button
        color='primary'
        className='btn-xs-block'
        onClick={() => {
          if (!isExternalApp(currentOrder?.canal) && followLink) {
            window.open(followLink, '_blank', 'noopener,noreferrer');
          } else {
            const modalData = {
              color: 'success',
              title: `Suivre le livreur ${currentOrder?.userNameDep ||
                '--'} ${currentOrder?.userLastNameDep || ''}`,
              header: true,
              children: <MapOrders isLoading={false} orders={[currentOrder]} />,
              width: '700px',
              center: true,
              modalBodyClass: 'justify-content-center d-flex pb-0',
              btnTxt: 'Retour',
            };
            dispatch(actions.showModal(modalData));
          }
        }}
      >
        Suivre le livreur
      </Button>
    );
  };

  const displayDriverDetail = (
    currentOrder,
    isCompleted,
    shouldHideOrderPhase,
  ) => {
    const isFleetDriver =
      !isCompleted &&
      currentOrder?.delivery?.nature?.toUpperCase() === FLEET &&
      currentOrder?.pickupStatus !==
      PICKUP_STATUS_KEY.DRIVER_ON_THE_WAY_TO_CUSTOMER;
    const isDeliveroo = !isCompleted && shouldHideOrderPhase;
    const estimatedHour =
      currentOrder?.expectedPickupDate && isDeliveroo
        ? `- Heure d'arrivée estimée ${suitableTime(
          moment(currentOrder?.expectedPickupDate),
        )}`
        : '';

    const driverIsIndependent = currentOrder?.delivery?.freelance;
    const deliveryNature = currentOrder?.delivery?.nature?.toUpperCase();
    const isFleet = deliveryNature === FLEET;

    let driverTitle = `COORDONNÉES LIVREUR ${deliveryNature || ''}`;
    if (isFleet && typeof driverIsIndependent === 'boolean') {
      driverTitle = driverIsIndependent
        ? 'COORDONNÉES LIVREUR INDÉPENDANT'
        : 'COORDONNÉES LIVREUR EMPLOYÉ';
    }

    return (
      <Paper
        className={`col-sm-12 col-md-12 col-lg-12`}
        style={
          currentOrder?.statusLabel === ORDER_PHASE_DELIVERING_LABEL &&
            !shouldHideOrderPhase
            ? classes.deliveryStyle
            : shouldHideOrderPhase
              ? !isAllShops
                ? !isCompleted
                  ? classes.deliveryDriverShopDetailsStyle
                  : classes.deliverySectionStyle
                : classes.deliveryDriverDetailsStyle
              : classes.deliverySectionStyle
        }
      >
        <div
          className='d-flex justify-content-between align-items-center'
          style={{ padding: 0 }}
        >
          <p
            style={
              currentOrder?.driversHistory?.some((driver) => driver.name)
                ? classes.driverTitle
                : classes.subtitle
            }
          >
            {driverTitle}
          </p>
          {currentOrder?.driversHistory?.some((driver) => driver.name) ? (
            renderHistoryButton(currentOrder)
          ) : (
            <div className='d-flex align-items-center justify-content-between'>
              {!isCompleted &&
                !isAllShops &&
                renderFollowDriverButton(currentOrder)}
            </div>
          )}
        </div>

        <div className='d-flex justify-content-between align-items-start'>
          <div>
            <ListItem
              title={`${currentOrder?.userNameDep ||
                '--'} ${currentOrder?.userLastNameDep || ''} ${estimatedHour}`}
              icon={faUser}
              noPadding
            />
            {currentOrder?.numeroDep && (
              <ListItem
                title={returnNumber(currentOrder?.numeroDep) || '--'}
                icon={faPhone}
                noPadding
              />
            )}
          </div>
        </div>

        {!isCompleted &&
          !isAllShops &&
          currentOrder?.driversHistory?.some((driver) => driver.name) && (
            <div className='d-flex justify-content-center mt-2'>
              {renderFollowDriverButton(currentOrder)}
              {isFleetDriver && (
                <div className='d-flex align-items-center'>
                  {renderCancelButton(currentOrder, {
                    typeCancel: 'retirer',
                    onClick: () => {
                      handleCancelOrder(
                        currentOrder,
                        DRIVER_CANCEL_REASSIGNMENT,
                        driversAvailability,
                        isAllShops,
                        setIsLoadingReception,
                        setIsLoadingDriver,
                      );
                    },
                    btnTxt: 'Changer le livreur',
                    color: 'danger',
                    title: `Êtes-vous sûr(e) de vouloir retirer la commande à ${currentOrder.userNameDep}?`,
                  })}
                </div>
              )}
            </div>
          )}
        {isDeliveroo && displayDeliverooMap(currentOrder)}
      </Paper>
    );
  };
  const displayDeliverooMap = (currentOrder) => {
    return (
      <>
        <div className='pb-2'>
          {currentOrder?.pickupStatus ===
            PICKUP_STATUS_KEY.DRIVER_ON_THE_WAY_TO_CUSTOMER && (
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '-7px',
                  }}
                >
                  <div style={{ width: '100%', fontWeight: 500 }}>
                    Code de validation client
                  </div>
                  <strong style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    {currentOrder?.validationCode}
                  </strong>
                </div>
                <p style={{ color: 'red', marginTop: '-10px' }}>
                  Si votre client perd son code de validation vous pouvez lui
                  fournir
                </p>
              </div>
            )}
        </div>
        <MapOrders
          style={{
            borderRadius: UNIT,
            overflow: 'hidden',
            width: '100%',
            height: '100%',
          }}
          isLoading={false}
          orders={[currentOrder]}
          isPaperDisplay={true}
          isExternalDriver={true}
          zoom={14}
          zoomDef={14}
        />
      </>
    );
  };
  const renderCancelsButtons = (currentOrder) => {
    const { statusLabel, shopResponsibleForDelivery, delivery } = currentOrder;
    const isFleetOrder = delivery?.jobId;
    const showButtons =
      isFleetOrder || shopResponsibleForDelivery
        ? true
        : currentOrder.pickupStatus === 0;
    const showRefundButton =
      showButtons &&
      canRefundOrder(currentOrder, distributeurAdmin, marketplace);
    const showCancelButton =
      showButtons &&
      (statusLabel === ORDER_PHASE_DELIVERING_LABEL ||
        statusLabel === ORDER_PHASE_TAKEAWAY_LABEL ||
        statusLabel === ORDER_PHASE_WAITER_LABEL ||
        statusLabel === ORDER_PHASE_PREPARING_LABEL);
    if (!showCancelButton && !showRefundButton) return null;
    return (
      <div className='row d-flex align-self-center align-items-center justify-content-center'>
        {showCancelButton && (
          <div
            className={`d-flex align-items-center justify-content-center ${showCancelButton ? 'col-lg-5 col-sm-6 p-0' : 'col-12'
              } ${statusLabel === ORDER_PHASE_DELIVERING_LABEL
                ? 'col-md-5'
                : 'col-md-12'
              }`}
          >
            {renderCancelButton(currentOrder, {
              typeCancel: 'annuler',
              onClick: () =>
                handleCancelOrder(
                  currentOrder,
                  CANCEL_CLIENT_UNAVAILABLE,
                  driversAvailability,
                  isAllShops,
                  setIsLoadingReception,
                  setIsLoadingDriver,
                ),
              btnTxt: 'Annuler',
              color: 'danger',
            })}
          </div>
        )}

        {showRefundButton && (
          <div
            className={`d-flex align-items-center justify-content-center col-lg-6 col-sm-12 p-0 ${statusLabel === ORDER_PHASE_DELIVERING_LABEL
              ? 'col-md-6'
              : 'col-md-12'
              }`}
          >
            {renderCancelButton(currentOrder, {
              typeCancel: 'rembourser',
              onClick: () =>
                handleCancelOrder(
                  currentOrder,
                  SHOP_CANCEL_AND_REFUND,
                  driversAvailability,
                  isAllShops,
                  setIsLoadingReception,
                  setIsLoadingDriver,
                ),
              btnTxt: 'Rembourser',
            })}
          </div>
        )}
      </div>
    );
  };

  const renderDriversHistory = (driversHistory) => {
    const sortedData = getSortedDrivers(driversHistory);
    return (
      <div style={{ height: 300, overflow: 'auto' }}>
        {sortedData.map((item, index) => {
          const formattedDate = moment(item.date).format('DD/MM/YYYY');
          const formattedTime = moment(item.date).format(
            'HH[h]mm [et] ss [secondes]',
          );
          return (
            <div key={index} style={{ padding: '5px 0px' }}>
              <span style={{ fontWeight: 'bold', fontSize: UNIT * 0.75 }}>
                {item.name}
              </span>{' '}
              est contacté
              <span style={{ fontWeight: 'normal' }}>
                {' '}
                par {METHODS[item.method] || item.method}
              </span>{' '}
              le {formattedDate} à{' '}
              <span style={{ fontWeight: 'bold', fontSize: UNIT * 0.75 }}>
                {formattedTime}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderPrintTicketIcon = (shopId, status, order, classes) => {
    return checkIntegrationAccess('order', 'hideprintTicket', shopId) ||
      status === ORDER_PHASE_CONTACTING ? null : (
      <div className='row justify-content-end'>
        <Item
          button
          onClick={() => printTicket(order, true, shop.hours ?? {})}
          style={classes.iconStyle}
        >
          <PrintTwoToneIcon fontSize='large' />
        </Item>
      </div>
    );
  };

  const renderOptions = (e) => {
    if (!e.items) return null;
    return _.map(_.orderBy(e.items, ['categoryPosition']), (item) => {
      if (!item) return null;

      const { options, categoryKey, categoryName: initialCategoryName } = item;
      const isChild = isChildCategory(categoryKey, e.items);
      const categoryName = isChild
        ? getFormattedCategoryName(item, e.items)
        : initialCategoryName;
      const optionLabels = getOptionLabels(options);

      return renderItem(categoryName, optionLabels, classes);
    });
  };

  const isChildCategory = (categoryKey, items) => {
    return _.some(
      items,
      (childItem) =>
        childItem?.categoryKey !== categoryKey &&
        categoryKey.includes(childItem?.categoryKey),
    );
  };

  const getFormattedCategoryName = (item, items) => {
    let { categoryName } = item;

    _.forEach(item.options, (option) => {
      const optionParent = findOptionParent(option, items);
      if (optionParent) {
        categoryName = formatCategoryName({
          shopCategory: { name: item.categoryName },
          parentItemLabel: optionParent.label,
          parentItemRecursiveKey: optionParent.itemRecursiveKey,
          isMultiple: optionParent.categoryMultiple,
        });
      }
    });

    return categoryName;
  };

  const getOptionLabels = (options) => {
    return _.map(
      options,
      (option) => `${option.label} (+${displayPriceFormat(option.price)})`,
    );
  };

  const renderItem = (categoryName, optionLabels, classes) => {
    const itemRenderString = `- ${categoryName}: ${optionLabels.join(', ')}`;

    return (
      <div
        key={Math.random()
          .toString(36)
          .substring(2, 15)}
      >
        <p style={{ display: 'inline-block', ...classes.textDetails }}>
          {itemRenderString}
        </p>
        <br />
      </div>
    );
  };

  return (
    <>
      <Modal
        color='secondary'
        title={`Détails de la commande N°: ${order.orderNumber} ${externalOrderNumber ? `(S-${externalOrderNumber})` : ''
          }`}
        header
        fixed
        size={isCompleted ? order.orderType === ORDER_TYPE_SEAT && 'md' : 'lg'}
        width={
          isCompleted
            ? !order.orderType === ORDER_TYPE_SEAT
              ? '700px'
              : '600px'
            : '750px'
        }
        onClick={() => {
          handleClose();
        }}
        btnColor='secondary'
        btnTxt='Retour'
        handleClose={() => {
          handleClose();
        }}
        isOpen={isOpen}
      >
        <div>
          <div className='row justify-content-center'>
            <div
              className={`${isDisplayInformations
                ? 'row col-12'
                : 'col-9 col-xs-9 col-sm-9 col-md-9 pl-0 justify-content-center'
                }`}
            >
              <div
                className={
                  isDisplayInformations
                    ? isDisplayInformations &&
                      !checkIntegrationAccess(
                        'order',
                        'hideprintTicket',
                        shopId,
                      ) &&
                      statusLabel !== ORDER_PHASE_LOOKING_FOR_DRIVER_LABEL
                      ? `col-md-6 col-sm-12 pl-0`
                      : 'col-md-7 col-sm-12 pl-0'
                    : 'col-12 pl-0'
                }
              >
                {isAllShops &&
                  shop.name &&
                  displayOrderDetail({
                    title: 'MAGASIN : ',
                    body: shop.name,
                  })}
                {displayOrderDetail({
                  title: 'ÉTAT : ',
                  color:
                    statusLabel === ORDER_PHASE_LOOKING_FOR_DRIVER_LABEL
                      ? mainColor.colorBlue
                      : isCompleted ||
                        statusLabel === ORDER_PHASE_CONTACTING_LABEL
                        ? statusLabel !== ORDER_COMPLETED_LABEL
                          ? mainColor.colorRed
                          : mainColor.colorGreen
                        : 'rgb(255, 180, 0)',
                  description:
                    cancellationBy &&
                    `(Par ${CANCELLATION_LABELS[cancellationBy] ||
                    CANCELLATION_LABELS[DRIVER]})`,
                  body: statusLabel,
                })}
                {displayOrderDetail({
                  title: 'TYPE : ',
                  body: SUITABLE_ORDER_TYPES[orderType],
                })}
                {displayOrderDetail({
                  title: 'CANAL : ',
                  body: getCanalName(canal),
                })}
                {displayOrderDetail({
                  title: 'DATE CLIENT :',
                  body:

                    <p
                      className='m-0'
                      style={{
                        width: isDisplayInformations
                          ? `${UNIT * 6.5625}%`
                          : `${UNIT * 8.625}%`,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <b>
                        {`${displayDate(date)}${suitableTime(
                          moment(date)
                        )} - ${suitableTime(
                          moment(date).add(
                            generalConfig?.hours?.slotInterval ?? DEFAULT_SLOT_INTERVAL, MOMENT_UNIT.MINUTE
                          )
                        )}`}
                      </b>
                    </p>
                  ,
                  subtitle: time,
                  isDate: true,
                })}
                {status === ORDER_PHASE_PREPARING && displayOrderDetail({
                  title: 'DATE CUISINE :',
                  body:
                    <p
                      className='m-0'
                      style={{
                        width: isDisplayInformations
                          ? `${UNIT * 6.5625}%`
                          : `${UNIT * 8.625}%`,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {isDelivery(orderType) ? 'Arrivée du livreur estimée' : 'Sortie de cuisine idéale'} <b>{time}</b>
                    </p>
                  ,
                  subtitle: time,
                  isDate: true,
                })}
              </div>
              {isDisplayInformations && (
                <>
                  <div
                    className='col-md-5 col-sm-12 d-flex flex-column py-3'
                    style={{
                      height: 'fit-content',
                      borderRadius: UNIT,
                      border: `1px solid`,
                      borderColor: `${setOpacity('255, 180, 0', 0.5)}`,
                      backgroundColor: `${setOpacity('255, 180, 0', 0.25)}`,
                      padding: UNIT * 0.875,
                    }}
                  >
                    <div className='mb-0 mb-1'>
                      <h5
                        className='bold-text'
                        style={{ color: mainColor.colorBlack }}
                      >
                        Information
                      </h5>
                    </div>
                    <div className=''>
                      <p style={{ color: mainColor.colorBlack, fontSize: 12 }}>
                        Nous continuons à chercher un livreur. Afin d'assurer la
                        prise en charge de la commande, nous avons également
                        proposé au client de la récupérer au restaurant.
                      </p>
                    </div>
                  </div>
                </>
              )}
              {isDisplayInformations && (
                <div className={'col-2 col-xs-2 col-sm-2 col-md-1'}>
                  {renderPrintTicketIcon(shopId, status, order, classes)}
                </div>
              )}
            </div>
            {!isDisplayInformations && (
              <div className={'col-2 col-xs-2 col-sm-2 col-md-2'}>
                {renderPrintTicketIcon(shopId, status, order, classes)}
              </div>
            )}
          </div>
          <div className='row d-flex justify-content-between p-2'>
            {!isCompleted ? (
              <div
                className='col col-sm-12 col-md-8 col-lg-8 pr-md-3  pr-lg-3 mb-4 mb-md-0 mb-lg-0'
                style={{ padding: 0, justifyContent: 'stretch' }}
              >
                {!shouldHideOrderPhase && (
                  <Paper
                    style={getPaperClassName(
                      order,
                      statusLabel,
                      classes,
                      isDisplayDriverDetails,
                      showOrderInPreparation,
                    )}
                  >
                    {!isLoadingReception ||
                      statusLabel === ORDER_PHASE_DELIVERING_LABEL ? (
                      <div>
                        {orderFlowType === ORDER_FLOW_TYPE.REVERSE &&
                          statusLabel === ORDER_PHASE_LOOKING_FOR_DRIVER_LABEL ? (
                          shouldDisplayLookingForDriver ? (
                            displayLookingForDriver(order)
                          ) : (
                            displaySearchingDriver(order)
                          )
                        ) : statusLabel === ORDER_PHASE_CONTACTING_LABEL ? (
                          displayPhaseOne(order)
                        ) : statusLabel === ORDER_PHASE_PREPARING_LABEL ? (
                          shouldDisplayScheduledMessage(order) &&
                            orderFlowType === ORDER_FLOW_TYPE.CLASSIC &&
                            showOrderInPreparation ? (
                            renderScheduledMessage(order)
                          ) : (isExternalApp(canal) &&
                            canal !== ORDER_DELIVEROO) ||
                            (canal === ORDER_DELIVEROO &&
                              isDelivery(orderType)) ? (
                            displayPhaseThree(order)
                          ) : (
                            displayPhaseTwo(order)
                          )
                        ) : statusLabel === ORDER_PHASE_TAKEAWAY_LABEL ||
                          statusLabel === ORDER_PHASE_DELIVERING_LABEL ||
                          statusLabel === ORDER_PHASE_WAITER_LABEL ? (
                          displayPhaseThree(order)
                        ) : (
                          <div> {displayPhaseFour(order)}</div>
                        )}
                      </div>
                    ) : (
                      <div
                        className={`d-flex h-100 justify-content-center align-items-center`}
                        style={
                          ((statusLabel === ORDER_PHASE_DELIVERING_LABEL &&
                            userNameDep) ||
                            isLoadingReception) &&
                          classes.spinnerStyle
                        }
                      >
                        {hasExternalIntegration(shopId) &&
                          !isLoadingReception ? (
                          <ScheduledOrderMessage
                            classes={classes}
                            order={order}
                            isFirstPhase={true}
                          />
                        ) : (
                          <Spinner />
                        )}
                      </div>
                    )}
                  </Paper>
                )}

                {shouldDisplaySearchingDriverSection(
                  order,
                  statusLabel,
                  showOrderInPreparation,
                ) && (
                    <Paper style={classes.deliverySectionStyle}>
                      {displaySearchingDriver(order)}
                    </Paper>
                  )}

                {isDisplayDriverDetails &&
                  userNameDep &&
                  displayDriverDetail(order, isCompleted, shouldHideOrderPhase)}
              </div>
            ) : null}
            <div
              className={`col-sm-12 col-md-${!isCompleted ? 4 : 12} col-lg-${!isCompleted ? 4 : 12
                }`}
              style={{ padding: 0, justifyContent: 'stretch' }}
            >
              <Paper style={classes.secondSectionStyle}>
                <p style={{ marginBottom: UNIT * 0.3125, ...classes.subtitle }}>
                  COORDONNÉES CLIENT
                </p>

                <div className='row'>
                  <div className='col col-sm'>
                    <div>
                      {orderType === ORDER_TYPE_SEAT && (
                        <ListItem title={tableNb || '--'} icon={faUtensils} />
                      )}
                      <ListItem
                        title={`${firstName || '--'} ${lastName.toUpperCase() ||
                          ''}`}
                        icon={faUser}
                      />
                      {isExternalApp(canal) &&
                        !isObjectNotEmpty(address) ? null : (
                        <ListItem
                          title={
                            isObjectNotEmpty(address)
                              ? getAddressFormatted(address)
                              : '--'
                          }
                          icon={faHome}
                        />
                      )}
                      {isCompleted && displayPaymentType(order)}

                      {!isCompleted && (
                        <>
                          {isExternalApp(canal) && !email ? null : (
                            <ListItem title={email || '--'} icon={faEnvelope} />
                          )}
                          {isExternalApp(canal) && !numero ? null : (
                            <ListItem title={numero || '--'} icon={faPhone} />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {isCompleted && (
                    <div className='col-sm '>
                      <ListItem title={email || '--'} icon={faEnvelope} />
                      <ListItem title={numero || '--'} icon={faPhone} />
                      {(statusLabel === ORDER_COMPLETED_LABEL ||
                        statusLabel === ORDER_CANCELED_LABEL) &&
                        !isExternalApp(canal) &&
                        displayPhaseFour(order)}
                    </div>
                  )}
                </div>
              </Paper>
              {!isCompleted && (
                <Paper style={classes.paymentSection}>
                  {displayPaymentType(order)}
                </Paper>
              )}
              {isCompleted &&
                userNameDep &&
                displayDriverDetail(order, isCompleted, shouldHideOrderPhase)}
            </div>
          </div>

          <div className='row' style={{ padding: '10px' }}>
            <div className='col' style={{ padding: 0 }}>
              <Paper style={classes.paper}>
                <p style={classes.margeDetail} />
                <div style={classes.detail}>
                  <p style={classes.subtitle}>RÉCAPITULATIF COMMANDE</p>
                  <br />
                  {_.map(order.commande || order.basket, (e) => {
                    return (
                      <div key={e.id}>
                        <OrderDetailLine
                          e={e}
                          classes={classes}
                          displayPriceFormat={displayPriceFormat}
                        />
                        <div
                          style={
                            e?.isLoyaltyProduct ? classes.orderDetailStyle : {}
                          }
                        >
                          <div style={{ width: e?.isLoyaltyProduct && '82%' }}>
                            {_.keys(e.items).length > 0
                              ? renderOptions(e)
                              : null}
                          </div>
                        </div>
                        {e?.isLoyaltyProduct && e?.price > 0 && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                            }}
                          >
                            +{' '}
                            {e.price
                              ? displayPriceFormat(e.price)
                              : displayPriceFormat('')}
                            <b>.Suppl</b>
                          </div>
                        )}
                        <p style={classes.textDetails}>
                          {e.instruction
                            ? `- Instructions: ${e.instruction}`
                            : null}
                        </p>
                        <hr style={classes.hrStyle} />
                      </div>
                    );
                  })}

                  {renderPromotionBloc({
                    order,
                    orderType,
                    reconversionType,
                    type,
                    promoCode,
                    managementFee,
                    userServicePrice,
                    displayPriceFormat,
                    mainColor,
                    classes,
                  })}

                  <hr style={classes.hrStyle} />
                  <p style={classes.totalStyle}>
                    <p style={classes.labelStyle}> TOTAL</p>
                    {displayPriceFormat(totalPriceOrder)}
                  </p>
                </div>
              </Paper>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
